// Trip status → Badge style-bridge — Phase 12 Plan 12-03 (UIR-04, R1).
//
// Research pattern R1 (Onfleet): ONE color encodes a trip's status everywhere
// (TripCard pill, Dashboard marker, TripDetails). We map trip.status/gps_status
// to a single TRIP_STATUS_COLORS key and expose a light/dark-aware fill so the
// telegram-ui Badge pill never hardcodes a hex (UI-SPEC §5 — no hardcode-hex).
//
// Dark-awareness: telegram-ui sets the AppRoot appearance from themeParams; in a
// WebView the OS color-scheme follows it. We read prefers-color-scheme so the
// pill picks `fillDark` in dark mode and `fill` in light — without depending on
// telegram-ui internal (hashed) class names.

import type { TripListItem } from "../api/trips";
import { DELAY_OVERLAY, TRIP_STATUS_COLORS, type TripStatusColor } from "./tokens";

/**
 * Map a trip list row to its single status color key (R1).
 *
 * Rules mirror the Dashboard marker FSM at list granularity (the list payload
 * lacks `gps_age`, so we cannot compute the yellow "stale" bucket here):
 *   - completed              → gray
 *   - cancelled / gps_lost   → red   (problem)
 *   - otherwise (active)     → green
 */
export function tripStatusColorKey(
  item: Pick<TripListItem, "status" | "gps_status">,
): TripStatusColor {
  if (item.status === "completed") return "gray";
  if (item.status === "cancelled" || item.gps_status === "gps_lost") return "red";
  return "green";
}

/**
 * Resolve the light/dark fill for a status color key.
 *
 * @param key   one of TRIP_STATUS_COLORS keys
 * @param dark  whether the current appearance is dark (default: matchMedia)
 */
export function tripStatusFill(key: TripStatusColor, dark: boolean = prefersDark()): string {
  const token = TRIP_STATUS_COLORS[key];
  return dark ? token.fillDark : token.fill;
}

/**
 * Resolve the gold delay-overlay fill for the current appearance (R1, UI-SPEC §5).
 *
 * This is the SEPARATE gold layer rendered on top of the base status color — it is
 * never a substitute for `tripStatusFill`. Callers paint the base status first, then
 * add this gold dot/marker only when the trip is delayed.
 *
 * @param dark  whether the current appearance is dark (default: matchMedia)
 */
export function delayOverlayFill(dark: boolean = prefersDark()): string {
  return dark ? DELAY_OVERLAY.fillDark : DELAY_OVERLAY.fill;
}

/** Detect dark appearance via prefers-color-scheme (jsdom-safe — defaults light). */
export function prefersDark(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  } catch {
    return false;
  }
}
